import { useState, useEffect, useCallback } from 'react';
import fcmService from '../services/fcmService';
import notificationApi from '../services/notificationApi';
import { toast } from 'react-toastify';

const useNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState({
    appointments: true,
    reminders: true,
    general: true,
    sound: true
  });
  const [unreadCount, setUnreadCount] = useState(0);

  // Initialize notification support check
  useEffect(() => {
    setIsSupported(fcmService.isSupported);
    setPermission(fcmService.getPermissionStatus());
  }, []);

  // Note: Removed automatic loading on mount to prevent unauthorized API calls
  // Data will be loaded when initialize() is called after authentication

  // Set up foreground message listener
  useEffect(() => {
    if (!isSupported) return;

    const unsubscribe = fcmService.onForegroundMessage((payload) => {
      console.log('Received foreground message:', payload);
      
      // Only update unread count if we have a token (user is authenticated and notifications are enabled)
      if (token) {
        notificationApi.getUnreadCount()
          .then(response => setUnreadCount(response.count || 0))
          .catch(error => console.error('Error updating unread count:', error));
      }
      
      // Show toast notification
      if (payload.notification) {
        toast.info(payload.notification.body, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    });

    return unsubscribe;
  }, [isSupported, token]);

  /**
   * Request notification permission and register token
   */
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    setIsLoading(true);
    
    try {
      const granted = await fcmService.requestPermission();
      setPermission(granted ? 'granted' : 'denied');
      
      if (granted) {
        // Update token directly without dependency
        try {
          const fcmToken = await fcmService.getToken();
          
          if (fcmToken) {
            setToken(fcmToken);
            localStorage.setItem('fcm_token', fcmToken);
            
            const deviceInfo = fcmService.getDeviceInfo();
            await notificationApi.registerToken(fcmToken, deviceInfo);
          }
        } catch (tokenError) {
          console.error('Error updating token:', tokenError);
          toast.error('Failed to register for notifications');
        }
        
        toast.success('Notifications enabled successfully!');
        return true;
      } else {
        toast.warning('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Failed to enable notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  /**
   * Get FCM token and register with backend
   */
  const updateToken = useCallback(async () => {
    if (!isSupported || permission !== 'granted') return;

    try {
      const fcmToken = await fcmService.getToken();
      
      if (fcmToken) {
        setToken(fcmToken);
        
        // Store token locally
        localStorage.setItem('fcm_token', fcmToken);
        
        // Register with backend
        const deviceInfo = fcmService.getDeviceInfo();
        await notificationApi.registerToken(fcmToken, deviceInfo);
        
        console.log('Token registered successfully');
      }
    } catch (error) {
      console.error('Error updating token:', error);
      toast.error('Failed to register for notifications');
    }
  }, [isSupported, permission]);

  /**
   * Load notification settings from backend
   */
  const loadSettings = useCallback(async () => {
    try {
      const response = await notificationApi.getSettings();
      if (response.settings) {
        setSettings(response.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Use default settings if API fails
    }
  }, []);

  /**
   * Update notification settings
   */
  const updateSettings = useCallback(async (newSettings) => {
    try {
      setSettings(newSettings);
      await notificationApi.updateSettings(newSettings);
      toast.success('Notification settings updated');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  }, []);

  /**
   * Load unread notification count
   */
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await notificationApi.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  }, []);

  /**
   * Send test notification
   */
  const sendTestNotification = useCallback(async () => {
    if (!token) {
      toast.warning('Please enable notifications first');
      return;
    }

    try {
      await notificationApi.sendTestNotification();
      toast.success('Test notification sent!');
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    }
  }, [token]);

  /**
   * Disable notifications and remove token
   */
  const disableNotifications = useCallback(async () => {
    try {
      if (token) {
        await notificationApi.removeToken(token);
        localStorage.removeItem('fcm_token');
        setToken(null);
      }
      
      toast.success('Notifications disabled');
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast.error('Failed to disable notifications');
    }
  }, [token]);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      // Refresh count directly without dependency
      const response = await notificationApi.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  /**
   * Initialize notifications (call after login)
   */
  const initialize = useCallback(async () => {
    if (!isSupported) return;

    // Check if user previously granted permission
    const currentPermission = fcmService.getPermissionStatus();
    setPermission(currentPermission);

    if (currentPermission === 'granted') {
      try {
        const fcmToken = await fcmService.getToken();
        
        if (fcmToken) {
          setToken(fcmToken);
          localStorage.setItem('fcm_token', fcmToken);
          
          const deviceInfo = fcmService.getDeviceInfo();
          await notificationApi.registerToken(fcmToken, deviceInfo);
        }
      } catch (error) {
        console.error('Error updating token during initialization:', error);
      }
    }

    // Load user settings and unread count
    try {
      const [settingsResponse, unreadResponse] = await Promise.all([
        notificationApi.getSettings().catch(() => ({ settings: null })),
        notificationApi.getUnreadCount().catch(() => ({ count: 0 }))
      ]);
      
      if (settingsResponse.settings) {
        setSettings(settingsResponse.settings);
      }
      setUnreadCount(unreadResponse.count || 0);
    } catch (error) {
      console.error('Error loading notification data during initialization:', error);
    }
  }, [isSupported]);

  return {
    // State
    isSupported,
    permission,
    token,
    isLoading,
    settings,
    unreadCount,
    
    // Actions
    requestPermission,
    updateToken,
    updateSettings,
    sendTestNotification,
    disableNotifications,
    markAsRead,
    initialize,
    loadUnreadCount,
    
    // Computed
    isEnabled: permission === 'granted' && !!token,
    canRequest: isSupported && permission === 'default'
  };
};

export default useNotifications;